import { Field, Formik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import * as Yup from "yup";

import { Alert, Button, Col, Form, Modal, Row } from "react-bootstrap";
import { IOrganisation } from "../../../../customTypings/company/Organisation";
import { IRole } from "../../../../customTypings/user/Role";
import { organisationService } from "../../../../services";
import { rolesService } from "../../../../services";
import { userService } from "../../../../services";
import { LoadingSpinner } from "../../../common/Loading/LoadingSpinner";
import {
    DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE,
    getUserFacingErrorMessage,
    logErrorForDev,
    shouldShowFormFieldError,
    showToastError,
} from "../../../../utils";

type AddNewUserProps = {
    show: boolean;
    onClose: () => void;
    onAdded?: () => void;
};

interface IActionResponse {
    status: string;
    message: string;
    entity?: any;
}

type InviteFormValues = {
    email: string;
    roles: string[];
    organisation: string;
};

/** Align with IdentityInviteViewModel (email, organisation required; roles expected). */
const inviteUserValidationSchema = Yup.object({
    email: Yup.string()
        .trim()
        .email("Enter a valid email address.")
        .max(256, "Email must be at most 256 characters.")
        .required("Email is required."),
    roles: Yup.array()
        .of(Yup.string().required())
        .min(1, "Select at least one role.")
        .required(),
    organisation: Yup.string().required("Select an organisation."),
});

const NO_ENABLED_ORGANISATIONS_MESSAGE = "There are no enabled organisations to invite a user into.";

const AddNewUser: React.FC<AddNewUserProps> = ({ show, onClose, onAdded }) => {
    const empty: string[] = [];
    const [alertVariant, setAlertVariant] = useState("danger");
    const [organisations, setOrganisations] = useState<IOrganisation[]>();
    const [organisationsLoadError, setOrganisationsLoadError] = useState<string | null>(null);
    const [allRoles, setAllRoles] = useState<IRole[]>();
    const [liveValidationAfterSubmit, setLiveValidationAfterSubmit] = useState(false);

    const validationSchema = useMemo(() => inviteUserValidationSchema, []);

    useEffect(() => {
        organisationService
            .getAll()
            .then((orgs: IOrganisation[]) =>
                setOrganisations(
                    orgs
                        .filter((organisation) => organisation.isEnabled && !organisation.isDeleted)
                        .sort((a, b) => a.displayName.localeCompare(b.displayName))
                )
            )
            .catch((e: unknown) => {
                logErrorForDev("AddNewUser.organisations", e);
                setOrganisationsLoadError(getUserFacingErrorMessage(e, "Could not load organisations."));
            });
        rolesService.getAllForGroup(0).then((roles: IRole[]) => setAllRoles(roles));
    }, []);

    // The form's initial organisation depends on the list, so it waits until the list has loaded.
    if (!organisations?.length) {
        const unavailableMessage = organisations ? NO_ENABLED_ORGANISATIONS_MESSAGE : organisationsLoadError;
        return (
            <Modal centered show={show} keyboard={false} onHide={onClose}>
                <Modal.Header closeButton>
                    <Modal.Title>Invite New User</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {unavailableMessage ? (
                        <Alert variant="danger" className="mb-0">
                            {unavailableMessage}
                        </Alert>
                    ) : (
                        <LoadingSpinner text="Loading organisations..." />
                    )}
                </Modal.Body>
            </Modal>
        );
    }

    // A single enabled organisation is assigned automatically; the picker is only shown when there is a choice.
    const showOrganisationPicker = organisations.length > 1;

    return (
        <Modal centered show={show} keyboard={false} onHide={onClose}>
            <Formik<InviteFormValues>
                initialValues={{
                    email: "",
                    roles: empty,
                    organisation: showOrganisationPicker ? "" : organisations[0].id,
                }}
                validationSchema={validationSchema}
                validateOnMount={false}
                validateOnChange
                validateOnBlur={false}
                onSubmit={async (values, { setStatus, setSubmitting }) => {
                    setLiveValidationAfterSubmit(true);
                    setStatus(undefined);
                    setSubmitting(true);
                    try {
                        const response = (await userService.add(
                            values.email,
                            values.roles,
                            values.organisation
                        )) as IActionResponse;
                        if (response.status === "Success") {
                            setAlertVariant("success");
                            setStatus("Invitation sent successfully.");
                            setLiveValidationAfterSubmit(false);
                            setTimeout(() => {
                                if (typeof onAdded === "function") onAdded();
                                onClose();
                            }, 3000);
                        } else {
                            setAlertVariant("danger");
                            setStatus(response.message);
                            showToastError(DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE);
                        }
                    } catch {
                        showToastError(DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE);
                    } finally {
                        setSubmitting(false);
                    }
                }}
            >
                {({ errors, status, isSubmitting, handleSubmit, submitCount }) => {
                    const hasPersistedRecord = false;
                    const showErr = (field: keyof InviteFormValues) =>
                        shouldShowFormFieldError(
                            errors,
                            field,
                            hasPersistedRecord,
                            submitCount,
                            liveValidationAfterSubmit
                        );
                    return (
                        <Form noValidate onSubmit={handleSubmit}>
                            <Modal.Header closeButton>
                                <Modal.Title>Invite New User</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <Form.Group as={Row} className="mb-3" controlId="newUserEmail">
                                    <Form.Label column sm={3}>
                                        Email <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Col sm={9}>
                                        <Field
                                            name="email"
                                            type="email"
                                            autoFocus
                                            className={`form-control${showErr("email") ? " is-invalid" : ""}`}
                                            aria-invalid={showErr("email")}
                                        />
                                        {showErr("email") && (
                                            <div className="invalid-feedback d-block">{errors.email}</div>
                                        )}
                                    </Col>
                                </Form.Group>
                                <Form.Group as={Row} className="mb-3" controlId="roleCheckboxs">
                                    <Form.Label column sm={3}>
                                        Roles <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Col sm={9} className="pt-2">
                                        {allRoles &&
                                            allRoles.map((role) => (
                                                <label key={role.id} className="d-block">
                                                    <Field
                                                        type="checkbox"
                                                        name="roles"
                                                        value={role.id}
                                                        className="form-check-input me-2"
                                                    />
                                                    {role.name}
                                                </label>
                                            ))}
                                        {showErr("roles") && (
                                            <div className="invalid-feedback d-block">{errors.roles as string}</div>
                                        )}
                                    </Col>
                                </Form.Group>
                                {showOrganisationPicker && (
                                    <Form.Group as={Row} className="mb-3" controlId="newUserOrganisation">
                                        <Form.Label column sm={3}>
                                            Organisation <span className="text-danger">*</span>
                                        </Form.Label>
                                        <Col sm={9}>
                                            <Field
                                                id="newUserOrganisation"
                                                name="organisation"
                                                as="select"
                                                className={`form-select${showErr("organisation") ? " is-invalid" : ""}`}
                                                aria-invalid={showErr("organisation")}
                                            >
                                                <option value="">Select an organisation…</option>
                                                {organisations.map((organisation) => (
                                                    <option key={organisation.id} value={organisation.id}>
                                                        {organisation.displayName}
                                                    </option>
                                                ))}
                                            </Field>
                                            {showErr("organisation") && (
                                                <div className="invalid-feedback d-block">{errors.organisation}</div>
                                            )}
                                        </Col>
                                    </Form.Group>
                                )}

                                {status && (
                                    <Alert variant={alertVariant} className="mt-3">
                                        {status}
                                    </Alert>
                                )}
                            </Modal.Body>
                            <Modal.Footer>
                                <div className="form-group">
                                    <Button variant="primary" disabled={isSubmitting} type="submit" className="me-2">
                                        {isSubmitting ? (
                                            <LoadingSpinner text="Sending invitation..." />
                                        ) : (
                                            "Invite User"
                                        )}
                                    </Button>
                                    <Button variant="secondary" onClick={onClose}>
                                        Cancel
                                    </Button>
                                </div>
                            </Modal.Footer>
                        </Form>
                    );
                }}
            </Formik>
        </Modal>
    );
};

export { AddNewUser };
